import React from 'react';

import {
  PublicFormMachineSendType,
  PublicFormMachineStateType
} from '../../../form/machines/PublicFormsMachine/types';
import { stepTypes } from '../../constants/FormConstant';
import { MultiStepFormHeaderProps } from '../../types/MultiStepRegistrationFormTypes';
import {
  getSuccessSlotDateTime,
  handleConditionBasedRedirection,
  isAuthenticationStep
} from '../../utils/MultiStepSuccessStepUtils';
import { EmbeddedFormSuccessMessage } from '../EmbeddedFormSuccessMessage';
import { EmbeddedFormSuccessPage } from '../EmbeddedFormSuccessPage';
import { GoogleCalendarInviteComponent } from '../GoogleCalendarInviteComponent';
import { MultiStepGoBackButton } from '../MultiStepGoBackButton';
import { MultiStepOtpHeading } from '../MultiStepOtpHeading';
import { MultiStepProgressBar } from '../MultiStepProgressBar';
import { SuccessMessageContainerWithEmailMessage } from '../SuccessMessageContainerWithEmailMessage';

import { CustomUIImage, FormDescription, FormTitle } from './styledComponents';

export const MultiStepFormHeader: React.FC<MultiStepFormHeaderProps> = ({
  eachFormStep,
  formValues,
  customClassNames,
  customHeader,
  getNumberedProgressBarTitles,
  renderCustomSuccessPage,
  publicFormMachineState,
  publicFormMachineSend,
  onBack,
  t
}) => {
  const { stepType: currentStepType, stepInfo, previousStepDetails } =
    eachFormStep;

  const {
    step_id: prevStepId,
    btn_id: prevBtnId,
    btn_text: prevBtnText
  } = previousStepDetails ?? {};

  const conditionBasedRedirectionUrls =
    publicFormMachineState.context.productInfo?.registrationMessagesConfig
      ?.success_payment.condition_based_redirection_urls;

  const renderCustomFormHeader = (): React.ReactElement | null =>
    customHeader ? customHeader(eachFormStep) : null;

  const renderGoogleCalendarInviteButton = (): React.ReactElement | null => {
    const { selectADateToBookASlot, timeSlots } = formValues;
    return (
      <GoogleCalendarInviteComponent
        selectADateToBookASlot={selectADateToBookASlot as string}
        timeSlots={timeSlots as string}
        stepInfo={stepInfo}
      />
    );
  };

  switch (currentStepType) {
    case stepTypes.EMBEDDED_SUCCESS_FORM:
      return (
        <>
          <MultiStepProgressBar
            stepInfo={stepInfo}
            getNumberedProgressBarTitles={getNumberedProgressBarTitles}
          />
          <EmbeddedFormSuccessMessage
            title={stepInfo?.title}
            description={stepInfo?.description}
            customClassNames={customClassNames}
          />
        </>
      );
    case stepTypes.EMBEDDED_SUCCESS_PAGE:
    case stepTypes.EMBEDDED_SUCCESS_PAGE_WITHOUT_SLOTS: {
      const { slotDate, slotTime } = getSuccessSlotDateTime(formValues);
      const isEmbeddedSuccessPageWithOutSlots =
        stepTypes.EMBEDDED_SUCCESS_PAGE_WITHOUT_SLOTS === currentStepType;

      return (
        <>
          <EmbeddedFormSuccessPage
            slotDate={
              isEmbeddedSuccessPageWithOutSlots ? undefined : slotDate
            }
            slotTime={
              isEmbeddedSuccessPageWithOutSlots ? undefined : slotTime
            }
            title={stepInfo?.title}
            description={stepInfo?.description}
            checkAndDoRedirection={() => {
              conditionBasedRedirectionUrls &&
                handleConditionBasedRedirection(
                  conditionBasedRedirectionUrls,
                  formValues
                );
            }}
          />
          {stepInfo?.calendar_invite_cta_details &&
            renderGoogleCalendarInviteButton()}
        </>
      );
    }
    case stepTypes.WEBINAR_CTA_SUCCESS_PAGE: {
      const { slotDate, slotTime } = getSuccessSlotDateTime(formValues);
      const email = formValues['email'] as string | undefined;

      return (
        <SuccessMessageContainerWithEmailMessage
          title={stepInfo?.title}
          description={stepInfo?.description}
          slotDate={slotDate}
          slotTime={slotTime}
          email={email}
        />
      );
    }
    case stepTypes.CUSTOM_SUCCESS_PAGE: {
      return renderCustomSuccessPage ? (
        renderCustomSuccessPage(
          formValues,
          publicFormMachineState as PublicFormMachineStateType,
          publicFormMachineSend as PublicFormMachineSendType
        )
      ) : (
        <></>
      );
    }
    default: {
      const customImageUrl = stepInfo?.custom_image_url;
      return (
        <>
          <MultiStepProgressBar
            stepInfo={stepInfo}
            getNumberedProgressBarTitles={getNumberedProgressBarTitles}
          />
          {(customHeader || stepInfo?.extra_header_ui_details) &&
            renderCustomFormHeader()}
          <MultiStepGoBackButton
            prevStepId={prevStepId}
            prevBtnId={prevBtnId}
            prevBtnText={prevBtnText}
            customClassNames={customClassNames}
            onBack={onBack}
          />
          {stepInfo?.title && (
            <FormTitle
              className={'public-registration-form-heading'}
              dangerouslySetInnerHTML={{ __html: stepInfo.title }}
            />
          )}
          {stepInfo?.description && (
            <FormDescription
              className={'public-registration-form-description'}
              dangerouslySetInnerHTML={{ __html: stepInfo.description }}
            />
          )}
          {customImageUrl && (
            <CustomUIImage
              src={customImageUrl}
              className="custom-image-url-class"
            />
          )}
          {isAuthenticationStep(currentStepType) && (
            <MultiStepOtpHeading
              phoneValue={formValues['phone'] as string}
              t={t}
            />
          )}
        </>
      );
    }
  }
};
